import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesProvider } from "@insite/content-library/additionalStyles";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    if (getProductListDataView(state).value) {
        return {
            isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        };
    }
    return {
        isEstimatedItemCount: false,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    accordionStyle?: InjectableCss;
    filterLogoImg?: InjectableCss;
    filterText?: TypographyProps;
    titleWrapper?: InjectableCss;
    tooltip?: TooltipPresentationProps;
}

const styles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            border: none;
            & dl {
                flex-basis: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        null,
                        css`
                            flex-basis: 100%;
                        `,
                    ])}
            }
            & dd {
                padding: 0;
                border: none;
            }
            & dt button {
                border-bottom: none;
                &:focus {
                    border: 1px solid #cccccc;
                    border-bottom: none;
                }
                &[aria-expanded="false"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                border: 1px solid #cccccc;
                            `,
                            css`
                                border: 1px solid #cccccc;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
                svg {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                        ])}
                }
            }
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    filterText: {
        css: css`
            font-size: 32px;
            font-weight: 500;
            color: #474747;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    titleWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            > div[class^="LazyImageStyle"] {
                width: 15%;
                margin-right: 10px;
            }
        `,
    },
    tooltip: {
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 7px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                    null,
                ])}
            `,
            tooltipClickable: css`
                padding: 0px 1px 3px !important;
                display: inline-flex !important;
                margin-bottom: 5px;
                border: none !important;
                background: transparent !important;
                justify-content: unset !important;
                svg {
                    display: unset !important;
                }
            `,
            tooltipBody: css`
                font-size: 14px;
                font-weight: normal;
                text-transform: none;
                line-height: 1.5;
            `,
        },
    },
};

export const columnsStyles = styles;

const ProductListColumns: FC<Props & ThemeProps<BaseTheme>> = ({ id, theme, isEstimatedItemCount }) => {
    const [isAccordion, setAccordion] = React.useState(false);
    const [isAccordionExpanded, setAccordionExpanded] = React.useState(false);
    const estimatedItemCountText = "Product counts are estimated and may update as you apply filters.";
    React.useEffect(() => {
        const setAccordionState = () => {
            if (window.innerWidth < theme.breakpoints.values[2]) {
                setAccordion(true);
            } else {
                setAccordion(false);
            }
        };
        setAccordionState();

        const onWindowResize = () => {
            setAccordionState();
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    });

    const onClickFilterSection = (_accordionExpanded: boolean) => () => {
        setAccordionExpanded(!_accordionExpanded);
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productListLeftCoulmn">
                <Accordion headingLevel={4} className="accordionFilterStyle">
                    <AccordionSection
                        title={
                            <StyledWrapper {...styles.titleWrapper}>
                                <LazyImage
                                    {...styles.filterLogoImg}
                                    src="/UserFiles/images/productlistingpage/filters.png"
                                ></LazyImage>
                                <Typography {...styles.filterText}>FILTERS</Typography>
                                {isEstimatedItemCount && (
                                    <Tooltip {...styles.tooltip} text={translate(estimatedItemCountText)} />
                                )}
                            </StyledWrapper>
                        }
                        {...(!isAccordion ? { expanded: true } : { expanded: isAccordionExpanded })}
                        onTogglePanel={onClickFilterSection(isAccordionExpanded)}
                    >
                        <Zone contentId={id} zoneName="Content00" />
                    </AccordionSection>
                </Accordion>
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(withTheme(ProductListColumns)),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
